<script setup>
import { ref, computed, watch } from 'vue'
import { useSalidaTokens } from '@/composables/useSalidaTokens'
import { TURNO_OPTIONS } from '@/utils/turno'

const props = defineProps({
  loading: Boolean,
  errorMsg: String,
  territorio: Object,
  terrKey: String,
  workedManzanas: { type: Array, default: () => [] },
  token: String,
  salidaFecha: [Number, String],
  salidaHora: String,
})

const { postWorkRecord } = useSalidaTokens()

const nameKey = computed(() => `salida-name-${props.token}`)
const userName = ref(localStorage.getItem(nameKey.value) || '')

const allManzanas = computed(() => {
  const mz = props.territorio?.mzNumbers
  if (!Array.isArray(mz) || mz.length <= 1) return []
  const result = []
  for (let i = 1; i < mz.length; i++) {
    if (mz[i]?.[0]) result.push(i)
  }
  return result
})

// Arranca con lo ya trabajado (por si otra persona ya marcó algo del mismo
// territorio); el usuario puede seguir marcando encima.
const worked = ref([])
watch(() => props.workedManzanas, (v) => { worked.value = [...(v || [])] }, { immediate: true })

const toggle = (i) => {
  worked.value = worked.value.includes(i)
    ? worked.value.filter((x) => x !== i)
    : [...worked.value, i]
}

const notas = ref('')
const saving = ref(false)
const success = ref(false)
const error = ref(null)
const turno = ref(null)

const submit = async () => {
  if (!userName.value.trim()) {
    error.value = 'Ingresá tu nombre.'
    return
  }
  if (!worked.value.length) {
    error.value = 'Marcá al menos una manzana trabajada.'
    return
  }
  if (!props.salidaHora && !turno.value) {
    error.value = 'Elegí en qué horario predicaron.'
    return
  }
  saving.value = true
  error.value = null
  localStorage.setItem(nameKey.value, userName.value.trim())
  const res = await postWorkRecord(props.token, {
    territoryKey: props.terrKey,
    conductor: userName.value.trim(),
    fecha: props.salidaFecha || Date.now(),
    notas: notas.value,
    manzanas: worked.value,
    ...(props.salidaHora ? {} : { turno: turno.value }),
  })
  saving.value = false
  if (res.error) {
    error.value = res.error
  } else {
    success.value = true
    notas.value = ''
  }
}
</script>

<template>
  <div class="simple-page">
    <img src="/territorios-icono.png" alt="Logo" class="simple-logo">

    <div v-if="loading" class="simple-loading">
      <i class="pi pi-spin pi-spinner" />
    </div>

    <div v-else-if="errorMsg" class="simple-error">{{ errorMsg }}</div>

    <div v-else-if="!territorio" class="simple-error">Territorio no encontrado.</div>

    <template v-else>
      <h1 class="simple-title">Territorio {{ terrKey }}</h1>

      <div v-if="success" class="simple-done">
        <i class="pi pi-check-circle" />
        <div>¡Trabajo registrado!<br>Gracias por informar.</div>
        <button class="simple-btn simple-btn-secondary" @click="success = false">Cargar otro registro</button>
      </div>

      <form v-else class="simple-form" @submit.prevent="submit">
        <label class="simple-field-label" for="simple-nombre">Tu nombre</label>
        <input
          id="simple-nombre"
          v-model="userName"
          type="text"
          class="simple-input"
          placeholder="Nombre y apellido"
        >

        <template v-if="allManzanas.length">
          <label class="simple-field-label mt">Manzanas trabajadas</label>
          <div class="simple-mz-grid">
            <button
              v-for="i in allManzanas"
              :key="i"
              type="button"
              class="simple-mz-btn"
              :class="{ active: worked.includes(i) }"
              @click="toggle(i)"
            >{{ i }}</button>
          </div>
        </template>

        <template v-if="!salidaHora">
          <label class="simple-field-label mt">¿En qué horario predicaron?</label>
          <div class="simple-turno-grid">
            <button
              v-for="opt in TURNO_OPTIONS"
              :key="opt.value"
              type="button"
              class="simple-turno-btn"
              :class="{ active: turno === opt.value }"
              @click="turno = opt.value"
            >{{ opt.label }}</button>
          </div>
        </template>

        <label class="simple-field-label mt" for="simple-notas">Notas (opcional)</label>
        <textarea id="simple-notas" v-model="notas" class="simple-textarea" rows="3" />

        <div v-if="error" class="simple-error-msg">{{ error }}</div>

        <button type="submit" class="simple-btn" :disabled="saving">
          {{ saving ? 'Registrando...' : 'Registrar trabajo' }}
        </button>
      </form>
    </template>
  </div>
</template>

<style scoped>
.simple-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px 48px;
  background: var(--bg-grouped, #f2f2f7);
  text-align: center;
}

.simple-logo {
  width: 112px;
  height: 112px;
  margin-bottom: 28px;
}

.simple-loading {
  font-size: 2rem;
  color: var(--accent, #007aff);
  margin-top: 40px;
}

.simple-error {
  font-size: 18px;
  color: var(--label-secondary, #666);
  margin-top: 20px;
  max-width: 420px;
}

.simple-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 24px;
}

.simple-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px;
  border-radius: 16px;
  background: var(--success-soft, #e3f7e8);
  color: var(--success-deep, #1e7d34);
  font-size: 18px;
  font-weight: 600;
  max-width: 420px;
}

.simple-done .pi {
  font-size: 2.4rem;
}

.simple-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  text-align: left;
}

.simple-field-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  margin-bottom: 8px;
}

.simple-field-label.mt {
  margin-top: 22px;
}

.simple-input,
.simple-textarea {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  padding: 14px 16px;
  font-size: 17px;
  color: var(--label-primary, #111);
}

.simple-textarea {
  resize: vertical;
  font-family: inherit;
}

.simple-mz-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
  gap: 10px;
}

.simple-mz-btn {
  height: 56px;
  border-radius: 12px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  color: var(--label-primary, #111);
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.simple-mz-btn.active {
  background: var(--success, #34c759);
  border-color: var(--success, #34c759);
  color: #fff;
}

.simple-turno-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 10px;
}

.simple-turno-btn {
  height: 48px;
  border-radius: 12px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  color: var(--label-primary, #111);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.simple-turno-btn.active {
  background: var(--accent, #007aff);
  border-color: var(--accent, #007aff);
  color: #fff;
}

.simple-error-msg {
  margin-top: 16px;
  color: var(--danger, #ff3b30);
  font-size: 15px;
  text-align: center;
}

.simple-btn {
  margin-top: 28px;
  width: 100%;
  padding: 18px;
  border: none;
  border-radius: 14px;
  background: var(--success, #34c759);
  color: #fff;
  font-size: 19px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.simple-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.simple-btn-secondary {
  margin-top: 4px;
  background: rgba(0, 0, 0, 0.12);
  color: var(--label-primary, #111);
}
</style>
